/*
 * KAPstat — Vital Pulse landing page.
 * Copyright (c) 2026 Eddlynn Edtech Private Limited. All rights reserved.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from './Wordmark.jsx';
import { ThemeToggle } from './ThemeToggle.jsx';
import { StudyGrid } from './StudyGrid.jsx';
import { SiteFooter } from './site.jsx';

const go = (id) => (e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

function Nav() {
  return (
    <nav className="nav">
      <Link to="/" className="nav__brand"><Wordmark height={22} light /></Link>
      <div className="nav__links">
        <a href="#how" onClick={go('how')}>How it works</a>
        <a href="#features" onClick={go('features')}>Features</a>
        <a href="#studies" onClick={go('studies')}>Studies</a>
        <Link to="/learn">Guidelines</Link>
        <a href="#faq" onClick={go('faq')}>FAQ</a>
      </div>
      <ThemeToggle />
      <Link to="/studies" className="btn btn--aqua nav__cta">Explore studies</Link>
    </nav>
  );
}

function PulseFlow() {
  return (
    <div className="pulse" role="img" aria-label="Knowledge to Attitude to Practice pulse">
      <svg viewBox="0 0 600 120" className="pulse__svg" preserveAspectRatio="none" aria-hidden="true">
        <path className="pulse__line" d="M0 60 H110 L140 60 L156 16 L178 104 L198 60 H300 L322 60 L338 22 L360 98 L380 60 H470 L494 60 L510 26 L532 96 L552 60 H600" />
      </svg>
      <div className="pulse__labels">
        <span className="pulse__lab pulse__lab--k">Knowledge</span>
        <span className="pulse__lab pulse__lab--a">Attitude</span>
        <span className="pulse__lab pulse__lab--p">Practice</span>
      </div>
    </div>
  );
}

function InteractiveDemo() {
  const [ans, setAns] = useState(null);
  const correct = 'no';
  return (
    <div className="demo">
      <div className="demo__tag">Try a real item</div>
      <p className="demo__q">A healthy patient has severe pain from <b>irreversible pulpitis</b> — no swelling, no fever. Are systemic antibiotics indicated?</p>
      <div className="demo__opts">
        {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => (
          <button key={v}
            className={'demo__opt' + (ans ? (v === correct ? ' is-correct' : (v === ans ? ' is-wrong' : '')) : '')}
            disabled={!!ans} onClick={() => setAns(v)}>{l}</button>
        ))}
      </div>
      {ans && (
        <div className={'demo__fb ' + (ans === correct ? 'ok' : 'no')}>
          <strong>{ans === correct ? 'Correct.' : 'Not quite.'}</strong> Antibiotics aren’t indicated — the fix is operative (pulpectomy/root-canal treatment).
          <span className="demo__micro">↳ That’s a KAPstat practice item. Participants learn the moment they answer.</span>
        </div>
      )}
    </div>
  );
}

const FEATURES = [
  ['📈', 'Measure change, not snapshots', 'A pre-test → education → post-test design is built in. See exactly what your teaching moved.'],
  ['🩺', 'Guideline-anchored items', 'Knowledge items and clinical vignettes built on current guidelines — not generic trivia.'],
  ['🔒', 'Anonymous & ethics-ready', 'No name, roll number or email. Row-Level Security, aggregate reporting, Declaration of Helsinki / ICMR aligned.'],
  ['🛡️', 'Test integrity by design', 'Answer keys never ship to the browser — scoring happens server-side, so no one can peek in the code.'],
  ['📊', 'Analysis-ready export', 'One click to CSV for SPSS or R, with pre and post automatically linked per participant.'],
  ['🧩', 'Reusable across topics', 'Spin up a new KAP study from a simple config — same rigor, any subject.'],
];

const STEPS = [
  ['1', 'Pre-test', 'Participants complete a short, anonymous KAP questionnaire — baseline knowledge, attitudes and practice.'],
  ['2', 'Learn', 'A focused, guideline-based micro-module appears between assessments — the intervention, built in.'],
  ['3', 'Post-test', 'The same items are re-administered. KAPstat quantifies the change and exports it, ready to analyse.'],
];

const FAQ = [
  ['Is it really anonymous?', 'Yes. Participants sign in anonymously — no name, roll number or email. Pre and post are linked by an anonymous ID, never an identity, and data are reported only in aggregate.'],
  ['Can I use my own questions?', 'Absolutely. Each study is a config — demographics, knowledge, attitude and practice items, plus an optional learning module. Add a new topic without touching the engine.'],
  ['How do participants join?', 'Share a study’s link or QR. It opens that study directly on any phone — no app install, no login. Perfect for handing to a cohort.'],
  ['Where does the data live?', 'In your own secure database (Supabase), isolated per study and protected by Row-Level Security. Export to CSV for SPSS/R anytime.'],
];

export default function Home() {
  return (
    <div className="landing">
      <Nav />

      <header className="hero">
        <div className="hero__glow" aria-hidden="true" />
        <div className="container hero__in">
          <div className="hero__copy">
            <p className="eyebrow">KAP research, reimagined</p>
            <h1 className="hero__title">Measure knowledge.<br /><span className="grad">Change practice.</span></h1>
            <p className="hero__sub">KAPstat is the platform for health &amp; medical <b>Knowledge–Attitude–Practice</b> studies — with education built in, so you don’t just survey behaviour, you improve it.</p>
            <div className="hero__cta">
              <Link to="/studies" className="btn btn--aqua btn--lg">Explore studies</Link>
              <a href="#how" onClick={go('how')} className="btn btn--ghostlight btn--lg">See how it works</a>
            </div>
            <div className="hero__trust">
              <span>Anonymous</span><i /><span>RLS-secure</span><i /><span>Guideline-anchored</span><i /><span>SPSS / R export</span>
            </div>
          </div>
          <div className="hero__art">
            <PulseFlow />
            <InteractiveDemo />
          </div>
        </div>
      </header>

      <section className="band-problem">
        <div className="container">
          <h2 className="section-title">KAP studies deserve better than a Google Form</h2>
          <div className="prob-grid">
            <div className="prob"><span className="prob__x">✕</span><b>No measured change.</b> A one-time form is a snapshot — it can’t show whether anyone learned anything.</div>
            <div className="prob"><span className="prob__x">✕</span><b>No teaching.</b> The chance to educate at the exact moment of a wrong answer is wasted.</div>
            <div className="prob"><span className="prob__x">✕</span><b>Leaky &amp; manual.</b> Answer keys sit in the form; linking pre/post and exporting is painful.</div>
          </div>
        </div>
      </section>

      <section id="how" className="band">
        <div className="container">
          <p className="eyebrow center">How it works</p>
          <h2 className="section-title center">Pre-test. Learn. Post-test.</h2>
          <div className="steps">
            {STEPS.map(([n, t, d]) => (
              <div className="step" key={n}>
                <div className="step__num">{n}</div>
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="band band--alt">
        <div className="container">
          <p className="eyebrow center">Why KAPstat</p>
          <h2 className="section-title center">Research rigour, without the overhead</h2>
          <div className="feat-grid">
            {FEATURES.map(([ic, t, d]) => (
              <div className="feat" key={t}>
                <div className="feat__ic" aria-hidden="true">{ic}</div>
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="studies" className="band">
        <div className="container">
          <p className="eyebrow center">Live studies</p>
          <h2 className="section-title center">Take one now — or share it with your cohort</h2>
          <p className="section-sub center">Each study opens anonymously on any phone. More studies are on the way.</p>
          <StudyGrid />
        </div>
      </section>

      <section className="band band--alt">
        <div className="container">
          <h2 className="section-title center">Built for everyone who teaches and learns</h2>
          <div className="who">
            {[['🎓', 'Students & interns', 'Quick, anonymous, and you actually learn something.'],
              ['🔬', 'Researchers', 'Rigorous pre/post design and clean, exportable data.'],
              ['🏫', 'Faculty & educators', 'See where the curriculum gaps are — and close them.'],
              ['✅', 'QI teams', 'Measure guideline uptake and the impact of training.']].map(([ic, t, d]) => (
              <div className="whocard" key={t}><span aria-hidden="true">{ic}</span><b>{t}</b><p>{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band">
        <div className="container cta-band__in">
          <h2>Start your first study in minutes.</h2>
          <p>No app to install. No login for participants. Just share a link.</p>
          <Link to="/studies" className="btn btn--coral btn--lg">Explore studies →</Link>
        </div>
      </section>

      <section id="faq" className="band">
        <div className="container container--narrow">
          <p className="eyebrow center">FAQ</p>
          <h2 className="section-title center">Good questions</h2>
          <div className="faq">
            {FAQ.map(([q, a]) => (
              <details className="faq__item" key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
